import React from 'react';
import styled from 'styled-components';
import { noop } from 'lodash';
import { makeStyles } from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import { Button, Dialog, TextField } from '@avangard/ui/core';
import { concatenateClasses } from '@avangard/ui/utils';

import { useEvent } from '@modules/shared/hooks';

import type { ButtonProps, TextFieldProps } from '@avangard/ui/core';

export type RemoveConfirmProps = {
    enable: boolean;
    title: string;
    content?: string;
};

type RemovableTextFieldRowProps = TextFieldProps & {
    onRemove: () => void;

    className?: string;
    RemoveConfirmProps?: RemoveConfirmProps;
    ButtonProps?: ButtonProps;
};

const useButtonStyles = makeStyles({
    root: {
        minWidth: 40,
        width: 40,
    },
    startIcon: {
        margin: 0,
    },
});

const Root = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    width: 100%;

    > *:first-child {
        margin-inline-end: 6px;
    }

    > *:last-child {
        width: calc(100% - 46px);
    }
`;

const deleteIcon = <Delete style={{ fontSize: 20 }} />;

const RemovableTextFieldRow = (props: RemovableTextFieldRowProps): React.ReactElement => {
    const { className, onRemove, RemoveConfirmProps, ButtonProps, ...otherProps } = props;

    const buttonClasses = concatenateClasses(useButtonStyles(), ButtonProps?.classes);

    const handleSafeRemove = useEvent(onRemove);
    const handleSafeButtonClick = useEvent(ButtonProps?.onClick ?? noop);

    const handleClick = React.useCallback(
        (event: React.MouseEvent<HTMLButtonElement>): void => {
            handleSafeButtonClick(event);

            onRemove();
        },
        [handleSafeButtonClick],
    );

    const renderedButton = React.useMemo(() => {
        if (!RemoveConfirmProps || !RemoveConfirmProps.enable) {
            return (
                <Button
                    {...ButtonProps}
                    startIcon={deleteIcon}
                    classes={buttonClasses}
                    onClick={handleClick}
                />
            );
        }

        return (
            <Dialog
                title={RemoveConfirmProps.title}
                content={RemoveConfirmProps.content}
                onAccept={handleSafeRemove}
            >
                {dialogProps => {
                    const { handleOpen } = dialogProps;

                    return (
                        <Button
                            {...ButtonProps}
                            startIcon={deleteIcon}
                            classes={buttonClasses}
                            onClick={handleOpen}
                        />
                    );
                }}
            </Dialog>
        );
    }, [RemoveConfirmProps, handleSafeRemove]);

    return (
        <Root className={className}>
            {renderedButton}

            <TextField {...otherProps} />
        </Root>
    );
};

export { RemovableTextFieldRow };
